// src/hooks/useStorageData.ts

import { useState, useCallback } from 'react';
import { StorageStats, StorageItem, DatabaseInfo, SyncQueueStatus } from '../types/storage.types';
import { withTimeout, formatBytes } from '../utils/storage.utils';
import { unifiedStorage } from '../../../../../helpers';

interface UseStorageDataReturn {
  stats: StorageStats | null;
  items: StorageItem[];
  databases: DatabaseInfo[];
  syncQueue: SyncQueueStatus;
  issues: string[];
  loading: boolean;
  loadStats: () => Promise<void>;
  loadItems: (searchText?: string, categoryFilter?: string) => Promise<void>;
  loadDatabases: () => Promise<void>;
  loadSyncStatus: (swController?: ServiceWorker) => Promise<void>;
  addIssue: (issue: string) => void;
  clearIssues: () => void;
  loadThumbnail: (key: string, setLoadingCallback?: (key: string, loading: boolean) => void) => Promise<void>;
  updateItemUrl: (key: string, url: string) => void;
}

export const useStorageData = (): UseStorageDataReturn => {
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [items, setItems] = useState<StorageItem[]>([]);
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [syncQueue, setSyncQueue] = useState<SyncQueueStatus>({
    pendingPosts: 0,
    lastSyncStatus: null,
  });
  const [issues, setIssues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const addIssue = useCallback((issue: string) => {
    setIssues(prev => [...prev, issue]);
  }, []);

  const clearIssues = useCallback(() => {
    setIssues([]);
  }, []);

  const loadStats = useCallback(async () => {
    try {
      console.log("🎯 Cargando estadísticas...");

      const storageStats = await withTimeout(
        unifiedStorage.getStorageStats(),
        5000,
        "storage stats"
      );

      let photos: any[] = [];
      let workflows: any[] = [];
      let syncQueueItems: any[] = [];

      // Cargar listas con manejo de errores individual
      try {
        photos = await withTimeout(unifiedStorage.listPhotos(), 5000, "listPhotos");
      } catch (error) {
        console.warn("⚠️ Error listando fotos:", error);
        addIssue("Error cargando lista de fotos");
      }

      try {
        workflows = await withTimeout(unifiedStorage.listWorkflows(), 5000, "listWorkflows");
      } catch (error) {
        console.warn("⚠️ Error listando workflows:", error);
        addIssue("Error cargando lista de workflows");
      }

      try {
        syncQueueItems = await withTimeout(unifiedStorage.listSyncQueue(), 5000, "listSyncQueue");
      } catch (error) {
        console.warn("⚠️ Error listando sync queue:", error);
        addIssue("Error cargando cola de sincronización");
      }

      // Calcular estadísticas
      let totalSize = 0;
      let oldestTimestamp = Number.MAX_SAFE_INTEGER;
      let newestTimestamp = 0;

      // Procesar datos de forma segura
      for (const photo of photos) {
        try {
          totalSize += photo.blob?.size || 0;
          oldestTimestamp = Math.min(oldestTimestamp, photo.timestamp || Date.now());
          newestTimestamp = Math.max(newestTimestamp, photo.timestamp || Date.now());
        } catch (error) {
          console.warn("⚠️ Error procesando foto:", error);
        }
      }

      // Obtener cuota del navegador
      let estimatedQuota: number | undefined;
      let usagePercent = 0;

      try {
        if (navigator.storage?.estimate) {
          const estimate = await withTimeout(navigator.storage.estimate(), 10000, "storage estimate");
          estimatedQuota = estimate.quota;
          usagePercent = estimatedQuota ? Math.round((totalSize / estimatedQuota) * 100) : 0;
        }
      } catch (error) {
        console.warn("⚠️ Error obteniendo cuota de storage:", error);
      }

      const finalStats: StorageStats = {
        totalItems: photos.length + workflows.length + syncQueueItems.length,
        photoItems: photos.length,
        workflowItems: workflows.length,
        syncQueueItems: syncQueueItems.length,
        otherItems: storageStats.byStore?.["cache-store"] || 0,
        totalSize,
        estimatedQuota,
        usagePercent,
        oldestTimestamp: oldestTimestamp !== Number.MAX_SAFE_INTEGER ? new Date(oldestTimestamp) : undefined,
        newestTimestamp: newestTimestamp > 0 ? new Date(newestTimestamp) : undefined,
        byStore: storageStats.byStore || {},
      };

      setStats(finalStats);

      // Detectar issues
      if (usagePercent > 80) {
        addIssue(`Almacenamiento casi lleno: ${usagePercent}%`);
      }

      console.log("✅ Estadísticas cargadas:", finalStats);
    } catch (error) {
      console.error("❌ Error cargando estadísticas:", error);
      addIssue("Error cargando estadísticas de almacenamiento");
      
      // Establecer estadísticas por defecto
      setStats({
        totalItems: 0,
        photoItems: 0,
        workflowItems: 0,
        syncQueueItems: 0,
        otherItems: 0,
        totalSize: 0,
        usagePercent: 0,
        byStore: {},
      });
    }
  }, [addIssue]);

  const loadItems = useCallback(async (searchText: string = "", categoryFilter: string = "all") => {
    try {
      console.log("🎯 Cargando elementos de storage...");
      const allItems: StorageItem[] = [];

      // Cargar cada tipo con manejo de errores individual
      try {
        const photos = await withTimeout(unifiedStorage.listPhotos(), 5000, "listPhotos para items");
        photos.forEach((photo: any) => {
          try {
            allItems.push({
              key: photo.id,
              size: photo.blob?.size || 0,
              type: photo.blob?.type || "image/unknown",
              category: "photo",
              url: null,
              lastModified: new Date(photo.timestamp).toLocaleString(),
              metadata: photo.metadata,
            });
          } catch (error) {
            console.warn("⚠️ Error procesando foto individual:", error);
          }
        });
      } catch (error) {
        console.warn("⚠️ Error cargando fotos para items:", error);
      }
      try {
        const workflows = await withTimeout(unifiedStorage.listWorkflows(), 5000, "listWorkflows para items");
        workflows.forEach((workflow: any) => {
          try {
            allItems.push({
              key: workflow.id,
              size: JSON.stringify(workflow.data || {}).length,
              type: "application/json",
              category: "workflow",
              url: null,
              lastModified: new Date(workflow.timestamp).toLocaleString(),
              metadata: { workflowType: workflow.type },
            });
          } catch (error) {
            console.warn("⚠️ Error procesando workflow individual:", error);
          }
        });
      } catch (error) {
        console.warn("⚠️ Error cargando workflows para items:", error);
      }

      try {
        const syncItems = await withTimeout(unifiedStorage.listSyncQueue(), 5000, "listSyncQueue para items");
        syncItems.forEach((item: any) => {
          try {
            allItems.push({
              key: item.id,
              size: JSON.stringify(item.data || {}).length,
              type: "application/json",
              category: "sync",
              url: null,
              lastModified: new Date(item.timestamp || Date.now()).toLocaleString(),
              metadata: item.metadata,
              status: item.status,
            });
          } catch (error) {
            console.warn("⚠️ Error procesando sync item individual:", error);
          }
        });
      } catch (error) {
        console.warn("⚠️ Error cargando sync queue para items:", error);
      }

      // Filtrar y ordenar de forma segura
      let filteredItems = allItems;

      if (searchText) {
        try {
          filteredItems = allItems.filter(
            (item) =>
              item.key.toLowerCase().includes(searchText.toLowerCase()) ||
              item.type.toLowerCase().includes(searchText.toLowerCase())
          );
        } catch (error) {
          console.warn("⚠️ Error aplicando filtro de búsqueda:", error);
        }
      }

      if (categoryFilter !== "all") {
        try {
          filteredItems = filteredItems.filter((item) => item.category === categoryFilter);
        } catch (error) {
          console.warn("⚠️ Error aplicando filtro de categoría:", error);
        }
      }

      try {
        filteredItems.sort((a, b) => b.size - a.size);
      } catch (error) {
        console.warn("⚠️ Error ordenando items:", error);
      }

      setItems(filteredItems);
      console.log("✅ Elementos cargados:", filteredItems.length);
    } catch (error) {
      console.error("❌ Error cargando elementos:", error);
      addIssue("Error cargando elementos del almacenamiento");
      setItems([]);
    }
  }, [addIssue]);

  const loadDatabases = useCallback(async () => {
    try {
      console.log("🎯 Cargando información de bases de datos...");

      if (!("databases" in indexedDB)) {
        addIssue("API de bases de datos no soportada");
        setDatabases([]);
        return;
      }

      const dbs = await withTimeout(indexedDB.databases(), 5000, "indexedDB.databases()");
      const dbInfos: DatabaseInfo[] = [];

      for (const dbInfo of dbs) {
        if (!dbInfo.name) continue;

        try {
          const db = await withTimeout(
            new Promise<IDBDatabase | null>((resolve, reject) => {
              const request = indexedDB.open(dbInfo.name!, dbInfo.version);
              request.onsuccess = () => resolve(request.result);
              request.onerror = () => reject(request.error);
              request.onblocked = () => resolve(null);
            }),
            10000,
            `open DB ${dbInfo.name}`
          );

          if (!db) {
            dbInfos.push({
              name: dbInfo.name,
              version: dbInfo.version || 0,
              stores: [],
              itemCount: 0,
              estimatedSize: 0,
              status: "error",
            });
            continue;
          }

          const stores = Array.from(db.objectStoreNames) as string[];
          let totalItems = 0;

          for (const storeName of stores) {
            try {
              const transaction = db.transaction(storeName, "readonly");
              const store = transaction.objectStore(storeName);
              const count = await withTimeout(
                new Promise<number>((resolve) => {
                  const countRequest = store.count();
                  countRequest.onsuccess = () => resolve(countRequest.result);
                  countRequest.onerror = () => resolve(0);
                }),
                2000,
                `count ${storeName}`
              );
              totalItems += count;
            } catch (error) {
              console.warn(`⚠️ Error contando ${storeName}:`, error);
            }
          }

          db.close();

          let status: "healthy" | "warning" | "error" = "healthy";
          if (totalItems === 0 && stores.length > 0) status = "warning";
          else if (stores.length === 0) status = "error";

          dbInfos.push({
            name: dbInfo.name,
            version: dbInfo.version || 0,
            stores,
            itemCount: totalItems,
            estimatedSize: totalItems * 1024,
            status,
          });
        } catch (error) {
          console.warn(`⚠️ Error diagnosticando DB ${dbInfo.name}:`, error);
          dbInfos.push({
            name: dbInfo.name,
            version: dbInfo.version || 0,
            stores: [],
            itemCount: 0,
            estimatedSize: 0,
            status: "error",
          });
        }
      }

      setDatabases(dbInfos);
      console.log("✅ Bases de datos cargadas:", dbInfos.length);
    } catch (error) {
      console.error("❌ Error cargando información de bases de datos:", error);
      addIssue("Error listando bases de datos");
      setDatabases([]);
    }
  }, [addIssue]);

  const loadSyncStatus = useCallback(async (swController?: ServiceWorker) => {
    if (!swController) {
      console.log("⚠️ Service Worker no disponible para sync check");
      return;
    }

    try {
      console.log("🎯 Verificando elementos pendientes...");

      // Verificar POST pendientes
      const postPromise = withTimeout(
        new Promise<void>((resolve) => {
          const channel = new MessageChannel();
          channel.port1.onmessage = (event) => {
            if (event.data.type === "PENDING_ITEMS_RESULT") {
              setSyncQueue((prev) => ({
                ...prev,
                pendingPosts: event.data.hasPending ? 1 : 0,
              }));
              resolve();
            }
          };
          swController.postMessage({ type: "CHECK_PENDING_ITEMS" }, [channel.port2]);
        }),
        10000,
        "check pending posts"
      );

      await Promise.allSettled([postPromise]);
      console.log("✅ Verificación de pendientes completada");
    } catch (error) {
      console.warn("⚠️ Error verificando elementos pendientes:", error);
      addIssue("Error verificando elementos de sincronización");
    }
  }, [addIssue]);

  // Función para actualizar URL de un item específico
  const updateItemUrl = useCallback((key: string, url: string) => {
    setItems(prev => 
      prev.map(item => 
        item.key === key ? { ...item, url } : item
      )
    );
  }, []);

  // Función para cargar thumbnail específico
  const loadThumbnail = useCallback(async (key: string, setLoadingCallback?: (key: string, loading: boolean) => void) => {
    if (setLoadingCallback) {
      setLoadingCallback(key, true);
    }

    try {
      const blob = await unifiedStorage.getPhoto(key);
      if (blob && blob.type.startsWith("image/")) {
        const url = URL.createObjectURL(blob);
        updateItemUrl(key, url);
      }
    } catch (error) {
      console.error(`Error cargando thumbnail para ${key}:`, error);
      addIssue(`Error cargando imagen: ${key}`);
    } finally {
      if (setLoadingCallback) {
        setLoadingCallback(key, false);
      }
    }
  }, [updateItemUrl, addIssue]);

  return {
    stats,
    items,
    databases,
    syncQueue,
    issues,
    loading,
    loadStats,
    loadItems,
    loadDatabases,
    loadSyncStatus,
    loadThumbnail,
    updateItemUrl,
    addIssue,
    clearIssues,
  };
};